import { Component, ElementRef, inject, input, OnChanges, OnDestroy, OnInit, signal, SimpleChanges, computed } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ProjectTreeNode } from '@models/project-tree-node.interface';
import { LookupDto } from '@models/lookup-dto.interface';
import { PROJECT_NODE_TYPE } from '@models/project-node-type.const';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { ProjectsService } from 'src/app/services/projects.service';
import { AssetClassesService } from 'src/app/services/asset-classes.service';
import { Subscription } from 'rxjs';
import { EditProjectComponent, EditProjectDialogData } from '../edit-project/edit-project.component';
import { TreeNodeComponent } from '../tree-node/tree-node.component';

@Component({
    selector: 'app-cvx-aside-left',
    imports: [TreeNodeComponent, MatIconModule, MatButtonModule, MatDialogModule, MatProgressSpinnerModule, CommonModule],
    templateUrl: './cvx-aside-left.component.html',
    styleUrl: './cvx-aside-left.component.scss'
})
export class CvxAsideLeftComponent implements OnInit, OnChanges, OnDestroy {
  private readonly projectsService = inject(ProjectsService);
  private readonly assetClassesService = inject(AssetClassesService);
  private readonly dialog = inject(MatDialog);
  private _el: ElementRef = inject(ElementRef);
  private router: Router = inject(Router);
  protected readonly NODE_TYPE = PROJECT_NODE_TYPE;
  protected years = signal<number[]>([]);
  protected selectedYear = signal<number | null>(null);
  protected assetClasses = signal<LookupDto[]>([]);
  protected selectedAssetClassId = signal<number | null>(null);
  protected isLoading = signal(false);
  protected selectedGrouping = signal<'org' | 'rig'>('org');
  protected hasResults = computed(() => this.visibleTree().length > 0);
  pExpanded = input<boolean>(false);
  externalData = input<ProjectTreeNode[] | null>(null);

  message: string = '';
  protected selectedKey = signal<string | null>(null);
  protected panelVisible = signal(false);
  dataTree = signal<ProjectTreeNode[]>([]);
  protected visibleTree = computed(() => {
    const isRigGrouping = this.selectedGrouping() === 'rig';
    return this.dataTree().filter(node => (node.key ?? '').startsWith('rig-') === isRigGrouping);
  });
  loading: boolean = false;
  private resetSub!: Subscription;
  private asideExpandedSub!: Subscription;
  private assetClassesChangedSub!: Subscription;
  private readonly parentLabelByKey = new Map<string, string>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['externalData'] && this.externalData() != null) {
      this.dataTree.set(this.externalData()!);
      this.buildParentMap(this.externalData()!);
      this.loading = false;
    }
  }

  ngOnInit(): void {
    this.loadAssetClasses();
    this.loadYears(false);
    this.asideExpandedSub = this.projectsService.asideExpanded$.subscribe((expanded) => {
      this.togglePanel(expanded, false);
    });
    this.assetClassesChangedSub = this.assetClassesService.changed.subscribe(() => this.loadAssetClasses());
    this.resetSub = this.projectsService.resetTree$.subscribe((navigate) => {
      const defaultYear = this.years()[0] ?? null;
      this.selectedYear.set(defaultYear);
      this.loadTree(navigate);
    });
  }

  ngOnDestroy(): void {
    this.resetSub?.unsubscribe();
    this.asideExpandedSub?.unsubscribe();
    this.assetClassesChangedSub?.unsubscribe();
  }

  protected onYearChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.selectedYear.set(value === '' ? null : Number(value));
    this.loadTree(true);
  }

  protected onAssetClassChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.selectedAssetClassId.set(value === '' ? null : Number(value));
    this.loadTree(true);
  }

  private loadAssetClasses(): void {
    this.projectsService.getAssetClasses().subscribe({
      next: (assetClasses: LookupDto[]) => {
        this.assetClasses.set(assetClasses);
        const selectedId = this.selectedAssetClassId();
        if (selectedId !== null && !assetClasses.some(a => a.id === selectedId)) {
          this.selectedAssetClassId.set(null);
          this.loadTree(false);
        }
      },
      error: () => this.assetClasses.set([])
    });
  }

  protected onGroupingChange(grouping: 'org' | 'rig'): void {
    this.selectedGrouping.set(grouping);
  }

  private loadYears(navigate: boolean = true): void {
    this.projectsService.getAvailableYears().subscribe({
      next: (years: number[]) => {
        this.years.set(years);
        const defaultYear = years[0] ?? null;
        this.selectedYear.set(defaultYear);
        this.loadTree(navigate);
      }
    });
  }

  // navigate=false is used for the passive initial load (e.g. on app bootstrap/page
  // refresh), so populating the sidebar tree doesn't hijack whatever route the user
  // is currently on (job details, worksheet, etc). navigate=true is used for explicit
  // user actions (year dropdown change, tree reset) where updating the /jobs route is
  // the intended behavior.
  private loadTree(navigate: boolean): void {
    this.isLoading.set(true);
    this.projectsService.getProjectTree(this.selectedYear(), this.selectedAssetClassId()).subscribe({
      next: (nodes: ProjectTreeNode[]) => {
        this.dataTree.set(nodes);
        this.buildParentMap(nodes);
        this.selectedKey.set(null);
        this.isLoading.set(false);
        if (navigate) {
          this.router.navigate(['/jobs'], {queryParams: { year: this.selectedYear() }});
        }
      },
      error: () => {
        this.dataTree.set([]);
        this.parentLabelByKey.clear();
        this.message = 'No projects found.';
        this.isLoading.set(false);
      }
    });
  }

  /** Builds a child-key → parent-label lookup so job nodes can resolve their project name. */
  private buildParentMap(nodes: ProjectTreeNode[], parentLabel: string | null = null): void {
    if (parentLabel === null) {
      this.parentLabelByKey.clear();
    }
    for (const node of nodes) {
      if (parentLabel !== null) {
        this.parentLabelByKey.set(node.key, parentLabel);
      }
      if (node.children?.length) {
        this.buildParentMap(node.children, node.label);
      }
    }
  }

  togglePanel(expanded?: boolean | null, withTransition: boolean = true) {

    const panel = this._el.nativeElement.querySelector('.cvx-slide-panel');

    if (panel) {
      if (!withTransition) {
        panel.classList.add('cvx-no-transition');
      } else {
        panel.classList.remove('cvx-no-transition');
      }
    }

    if (expanded === undefined || expanded === null) {
      this.panelVisible.set(!this.panelVisible());
    }
    else {
      this.panelVisible.set(expanded);
    }

    if (panel) {
      if (this.panelVisible()) {
        panel.classList.remove('cvx-hidden-content');
      }
      else {
        panel.classList.add('cvx-hidden-content');
      }
    }
  }

  expandAll() {
    this.visibleTree().forEach((node) => {
      this.expandRecursive(node, true);
    });
    this.dataTree.set([...this.dataTree()]);
  }

  collapseAll() {
    this.visibleTree().forEach((node) => {
      this.expandRecursive(node, false);
    });
    this.dataTree.set([...this.dataTree()]);
  }

  private expandRecursive(node: ProjectTreeNode, isExpand: boolean) {
    node.isExpanded = isExpand;
    if (node.children) {
      node.children.forEach((childNode) => {
        this.expandRecursive(childNode, isExpand);
      });
    }
  }

  protected openCreateDialog(): void {
    const dialogData: EditProjectDialogData = { mode: 'create' };
    const ref = this.dialog.open(EditProjectComponent, {
      data: dialogData,
      disableClose: true,
      autoFocus: true,
      width: '740px',      maxWidth: '90vw',    });

    ref.afterClosed().subscribe((newProjectId: number | false) => {
      if (newProjectId) {
        this.projectsService.triggerReset();
      }
    });
  }

  protected onNodeSelected(node: ProjectTreeNode): void {
    this.selectedKey.set(node.key);
    const data = node.data;
    switch (data.nodeType) {
      case PROJECT_NODE_TYPE.LOOKUP:
        this.router.navigate(['/jobs'], { queryParams: { key: node.key, projectName: node.label, nodeType: data.nodeType, year: this.selectedYear() } });
        break;

      case PROJECT_NODE_TYPE.PROJECT:
        this.router.navigate(['/jobs'], {
          queryParams: { nodeType: data.nodeType, projectId: data.entityId, projectName: node.label, year: this.selectedYear() }
        });
        break;

      case PROJECT_NODE_TYPE.JOB:
        if (data.entityId != null) {
          this.router.navigate(['/jobs', data.entityId], {
            queryParams: { projectName: this.parentLabelByKey.get(node.key) ?? null }
          });
        }
        break;
    }
  }
}
